import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import TestletListTile from '../widgets/TestletListTile';
import SearchBox from '../components/search/SearchBox';
import { Row } from 'reactstrap';
import InlineFilters from '../components/filters/InlineFilters';
import TestletService from '../services/Testlet/TestletService';
import CollaborationService from '../services/Collaboration/CollaborationService';
import { UserContext } from '../../src/Contexts';
import {
  convertFilterOptionsToAlgoliaAttributes,
  getAppliedFilterSummary,
  isFeatureActive,
  isUserAccessOfTypeAdmin
} from '../helpers/inbdeUtils';
import DisplayTestlets from '../components/testlet/DisplayTestlets';
import { concat } from 'lodash';
import { downloadFileFromUrl, isIterableArray } from '../helpers/utils';
import { buildRandomSet } from '../helpers/randomSet';
import ScrollDownTooltip from '../components/common/ScrollDownTooltip';
import { isEmpty } from 'lodash';
import { contentTypes } from '../data/content/contentTypes';
import { defaultExportFormat } from '../data/content/exportFormats';

import { ViewContentSummaryCard } from '../components/content/content';
import { customContentRoutes, searchPath } from '../routes';
import RandomSetBar from '../components/search/RandomSetBar';
import DialogBox from '../components/common/DialogBox';
import featureToggles from '../services/Feature/toggles';
import { toast } from 'react-toastify';
import { ContentService } from '../services/Content/content';
import RandomExportService from '../services/RandomExport/RandomExportService';

const filterQuery =
  'is_deleted=0 AND is_flagged=0 AND (testlet_type:3 OR testlet_type:4 OR testlet_type:5) AND is_public=1 AND is_latest_version=1';
let newFilterQuery = filterQuery;
const resultsPerPage = 10;
const searchPageStart = 0;
const randomPoolLimit = 1000; // the most the search service returns for one query
const matchCountOnly = 0; // asks for the count without shipping any records back
const largeSetQuestionCount = 50; // past this, collecting and zipping the images can take minutes

class SearchPage extends Component {
  allowedTypes = ['3', '4', '5']; // import json enum and see if open to search
  _isMounted = false;
  testletService = new TestletService();
  collaborationService = new CollaborationService();
  contentService = new ContentService();
  randomExportService = new RandomExportService();
  searchPageLimit = 10;
  // counts every search, filter and clear, so that the reset clearSearch schedules
  // two seconds ahead can tell whether something newer has happened since
  searchRun = 0;

  constructor(props) {
    super(props);

    this.state = {
      appliedFilters: [],
      loading: false,
      searching: false,
      query: '',
      searchParamsAndOptions: {},
      searchResults: null,
      testlets: [],
      hasMore: true,
      hasMoreSearch: true,
      lastTestlet: null,
      matchCount: null,
      randomSet: null,
      randomLoading: false,
      randomExportOpen: false,
      randomExporting: false
    };

    this.clearRandomSet = this.clearRandomSet.bind(this);
    this.clearSearch = this.clearSearch.bind(this);
    this.exportRandomSet = this.exportRandomSet.bind(this);
    this.filterTestlets = this.filterTestlets.bind(this);
    this.flagTestlet = this.flagTestlet.bind(this);
    this.getSearchResults = this.getSearchResults.bind(this);
    this.loadMore = this.loadMore.bind(this);
    this.pickRandomSet = this.pickRandomSet.bind(this);
    this.rerollRandomSet = this.rerollRandomSet.bind(this);
    this.toggleRandomExport = this.toggleRandomExport.bind(this);
    this.refreshPage = this.refreshPage.bind(this);
    this.searchTestlets = this.searchTestlets.bind(this);
    this.unFlagTestlet = this.unFlagTestlet.bind(this);
    this.updateContent = this.updateContent.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;

    this.getTestlets();
    this.refreshMatchCount();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  clearSearch(clearType) {
    const run = (this.searchRun += 1);

    this._isMounted && this.setState({ randomSet: null });
    const { query, searchParamsAndOptions } = this.state;
    searchParamsAndOptions['page'] = searchPageStart;
    const newState = {
      loading: true,
      query,
      searchParamsAndOptions
    };

    const searching =
      query !== '' && (searchParamsAndOptions['facetFilters'] && !isEmpty(searchParamsAndOptions['facetFilters']));
    if (clearType === 'search') {
      newState['query'] = '';
    } else {
      newFilterQuery = filterQuery;
      searchParamsAndOptions['filters'] = newFilterQuery;
      delete searchParamsAndOptions['facetFilters'];
      newState['searchParamsAndOptions'] = searchParamsAndOptions;
      newState['appliedFilters'] = [];
    }
    this._isMounted && this.setState({ ...newState });
    setTimeout(() => {
      // unticking the last filter and ticking another within the two seconds used to
      // put the whole list back over the newly filtered one
      if (run !== this.searchRun) {
        return;
      }

      if (searching) {
        this.getSearchResults(newState['query'], newState['searchParamsAndOptions'], true);
      } else {
        this._isMounted &&
          this.setState({ loading: false, searching: false, searchResults: null, hasMoreSearch: true });
        this.refreshMatchCount();
      }
    }, 2000);
  }

  async filterTestlets(filterOptions, appliedFilters) {
    this.searchRun += 1;

    this._isMounted &&
      this.setState({
        appliedFilters: getAppliedFilterSummary(appliedFilters),
        loading: true,
        searching: true,
        randomSet: null
      });

    const { query, searchParamsAndOptions } = this.state;
    const { filterAttributes, updatedNewFilterQuery } = convertFilterOptionsToAlgoliaAttributes(
      filterOptions,
      newFilterQuery,
      filterQuery
    );

    if (!!filterAttributes.length) {
      searchParamsAndOptions['facetFilters'] = filterAttributes;
      searchParamsAndOptions.filters = updatedNewFilterQuery;
      searchParamsAndOptions['hitsPerPage'] = resultsPerPage;
      searchParamsAndOptions['page'] = searchPageStart;

      this._isMounted && this.setState({ searchParamsAndOptions });

      this.getSearchResults(query, searchParamsAndOptions, true);
      newFilterQuery = updatedNewFilterQuery;
      newFilterQuery = filterQuery;
    } else {
      this.clearSearch();
    }
  }

  async flagTestlet(id, comment, collaborators, creatorId, testletName) {
    const { user, admins } = this.context;
    const isTestletFlagged = await this.testletFlagMechanism(id, true);

    if (isTestletFlagged) {
      this.collaborationService.rejectTestletComment(id, user, comment);
      this.collaborationService.sendAdminEmailForTestletFlagged(testletName, admins, comment, user, 'Flagged');
    }
    return isTestletFlagged;
  }

  loadMore() {
    const { hasMore, hasMoreSearch, query, searching, searchParamsAndOptions, searchResults } = this.state;

    if (!searchResults && hasMore) {
      this.getTestlets();
    } else if (searching && hasMoreSearch) {
      searchParamsAndOptions['page'] += 1;
      this._isMounted && this.setState({ searchParamsAndOptions: searchParamsAndOptions });
      this.getSearchResults(query, searchParamsAndOptions, false);
    }
  }

  async unFlagTestlet(id) {
    const isTestletUnflagged = await this.testletFlagMechanism(id, false);
    return isTestletUnflagged;
  }

  async testletFlagMechanism(id, flag) {
    const isTestletMarked = await this.testletService.markTestletAsFlagged(id, flag);
    return isTestletMarked;
  }

  async getTestlets() {
    this.setState({ loading: true });
    const { lastTestlet, testlets } = this.state;

    const listOfTestlets = await this.testletService.getPublicTestlets(lastTestlet, this.searchPageLimit);

    if (isIterableArray(listOfTestlets)) {
      if (listOfTestlets.length < this.searchPageLimit) {
        this._isMounted && this.setState({ hasMore: false });
      }

      const newTestlets = this.renderTestlets(listOfTestlets, false);
      const updatedTestlets = concat(testlets, newTestlets);

      this._isMounted && this.setState({ testlets: updatedTestlets });
    } else {
      this._isMounted && this.setState({ hasMore: false });
    }

    this._isMounted && this.setState({ loading: false });
  }

  async getSearchResults(query, options, isNewSearch) {
    let { hasMoreSearch, searchResults: records } = this.state;
    if (records === null) {
      records = [];
    }

    if (isNewSearch) {
      hasMoreSearch = true;
      records = [];
    }

    const searchResults = await this.testletService.searchForTestlets(query, options);
    if (searchResults) {
      if (searchResults.length < resultsPerPage) {
        hasMoreSearch = false;
      }

      const results = this.renderTestlets(searchResults, true);
      this._isMounted && this.setState({ searchResults: [...records, ...results], loading: false, hasMoreSearch });
      isNewSearch && this.refreshMatchCount();
    } else {
      toast.error('There was an error loading search results. Please try again later');
      this._isMounted && this.setState({ searchResults: records, loading: false, hasMoreSearch: false });
    }
  }

  refreshPage() {
    const { history } = this.props;

    this._isMounted &&
      this.setState(
        {
          appliedFilters: [],
          query: '',
          searchParamsAndOptions: {},
          searchResults: null,
          testlets: [],
          hasMore: true,
          lastTestlet: null
        },
        () => {
          history.push(searchPath);
        }
      );
  }

  // list of JS objects
  renderTestlets(list, isSearch) {
    const testlets = [];
    const { user } = this.context;
    const { access_type, uid } = user;
    const isUserAdmin = isUserAccessOfTypeAdmin(access_type);

    for (let i = 0; i < list.length; i += 1) {
      let id, data;
      const doc = list[i];
      if (isSearch) {
        id = doc.testlet_id || doc.objectID;
        data = doc;
        doc.testlet_id && delete doc['testlet_id'];
        delete data.objectID;
        delete data._exams;
        delete data._questionCount;
        delete data._mock_exams;
      } else {
        id = doc.id;
        data = doc.data();

        if (i === list.length - 1) {
          this._isMounted && this.setState({ lastTestlet: doc });
        }
      }
      data.id = id;

      let contentViewType;
      const { content_type } = data;
      if (content_type === contentTypes.question.type) {
        contentViewType = (
          <ViewContentSummaryCard
            key={id}
            contentData={data}
            contentId={id}
            handleContentAction={this.updateContent}
            isUserAdmin={isUserAdmin}
            pageTitle={'search'}
            userId={uid}
          />
        );
      } else {
        contentViewType = (
          <TestletListTile
            key={id}
            testlet={data}
            review={false}
            flagTestlet={this.flagTestlet}
            unFlagTestlet={this.unFlagTestlet}
            refreshPage={this.refreshPage}
            updateContent={this.updateContent}
          />
        );
      }

      testlets.push(contentViewType);
    }

    return testlets;
  }

  async searchTestlets(searchRequest) {
    this.searchRun += 1;

    this._isMounted && this.setState({ loading: true, searching: true, randomSet: null });
    const { searchParamsAndOptions } = this.state;
    const { options, query } = searchRequest;
    const { isSearchAll, params, contentTypeFilters } = options;

    const searchParams = isSearchAll ? [] : params;
    const searchFilterOptions = contentTypeFilters ? newFilterQuery + ' AND ' + contentTypeFilters : newFilterQuery;
    const newSearchParamsAndOptions = {
      restrictSearchableAttributes: searchParams,
      hitsPerPage: resultsPerPage,
      filters: searchFilterOptions,
      facetFilters: searchParamsAndOptions.facetFilters ? searchParamsAndOptions.facetFilters : [],
      page: searchPageStart
    };

    this._isMounted && this.setState({ query: query, searchParamsAndOptions: newSearchParamsAndOptions });
    this.getSearchResults(query, newSearchParamsAndOptions, true);
  }

  /*
   * Random set
   *
   * The pool comes from the search service rather than from the cards already on
   * screen: that list is paginated and ordered by `updated_on`, so sampling it
   * would quietly bias the draw towards recently edited content.
   */
  getRandomPoolParams() {
    const { searchParamsAndOptions } = this.state;
    const { facetFilters, filters, restrictSearchableAttributes } = searchParamsAndOptions;

    return {
      facetFilters: facetFilters || [],
      filters: filters || newFilterQuery,
      restrictSearchableAttributes
    };
  }

  async refreshMatchCount() {
    const { query } = this.state;
    const pool = await this.testletService.searchPool(query, this.getRandomPoolParams(), matchCountOnly);

    this._isMounted && this.setState({ matchCount: pool ? pool.matchCount : null });
  }

  async pickRandomSet(requested) {
    this._isMounted && this.setState({ randomLoading: true });

    const { query } = this.state;
    const pool = await this.testletService.searchPool(query, this.getRandomPoolParams(), randomPoolLimit);

    if (!pool) {
      toast.error('There was an error building a random set. Please try again later');
      this._isMounted && this.setState({ randomLoading: false });
      return;
    }

    // matching nothing is an answer, not a failure, and the count is corrected
    // here in case the index moved on since it was last read
    if (!isIterableArray(pool.hits)) {
      toast.error('No questions match this search, so there is nothing to draw from');
      this._isMounted && this.setState({ matchCount: pool.matchCount, randomLoading: false });
      return;
    }

    // buildRandomSet reads _questionCount, which renderTestlets strips, so draw
    // first and keep the records the export needs alongside the cards
    const drawn = buildRandomSet(pool.hits, requested);
    const elements = this.renderTestlets(drawn.items, true);

    this._isMounted &&
      this.setState({
        matchCount: pool.matchCount,
        randomLoading: false,
        randomSet: {
          contentIds: drawn.items.map(item => item.id).filter(Boolean),
          elements,
          isOnlySet: drawn.isOnlySet,
          questionCount: drawn.questionCount,
          requested,
          standAloneCount: drawn.standAloneCount,
          testletCount: drawn.testletCount
        }
      });
    window.scrollTo(0, 0);
  }

  rerollRandomSet() {
    const { randomSet } = this.state;
    randomSet && this.pickRandomSet(randomSet.requested);
  }

  clearRandomSet() {
    this._isMounted && this.setState({ randomSet: null });
    window.scrollTo(0, 0);
  }

  toggleRandomExport() {
    const { randomExporting } = this.state;

    // never strand a half finished export behind a closed dialog
    if (randomExporting) {
      return;
    }

    this._isMounted && this.setState(state => ({ randomExportOpen: !state.randomExportOpen }));
  }

  async exportRandomSet(exportFormat, setName) {
    const { randomSet } = this.state;
    const { toggles, user } = this.context;

    if (!randomSet) {
      return;
    }

    this._isMounted && this.setState({ randomExporting: true });

    try {
      const result = await this.contentService.exportRandomQuestions(randomSet.contentIds, exportFormat, user, toggles);

      if (result === null) {
        return;
      }

      if (result.isEmpty) {
        toast.error('None of these questions are approved for export');
      } else if (result.isSuccess) {
        // the archive first: a local run has already downloaded it directly, and
        // keeping the row for it behind the download keeps the wait off the user
        result.downloadUrl && downloadFileFromUrl(result.downloadUrl);
        toast.success(`Exported ${result.count} questions as ${exportFormat.toUpperCase()}`);
        this._isMounted && this.setState({ randomExportOpen: false });
        await this.recordRandomExport(result, setName);
      } else {
        throw new Error('there was an error');
      }
    } catch (error) {
      const reason = error && error.message ? ' because ' + error.message : '';
      toast.error(`This random set could not be exported${reason}`);
    }

    this._isMounted && this.setState({ randomExporting: false });
  }

  /*
   * Keep a row for the archive that was just built.
   *
   * The set was drawn here, so this is the only place that knows what it was
   * drawn from. Without the row the archive stays in storage with nothing
   * pointing at it, which is what the Random Sets page exists to fix.
   */
  async recordRandomExport(result, setName) {
    const { user } = this.context;
    const { appliedFilters, query } = this.state;

    const savedExport = await this.randomExportService.saveRandomExportToFirestore(result.id, {
      createdBy: user,
      filters: appliedFilters,
      format: result.format,
      keywords: query,
      name: setName,
      path: result.path,
      questionCount: result.count,
      url: result.downloadUrl
    });

    if (!savedExport) {
      toast.error('This set was exported but could not be added to Random Sets');
    }
  }

  async updateContent(contentId, contentAction, contentData, exportFormat = defaultExportFormat) {
    const { history } = this.props;
    const { toggles, user } = this.context;

    try {
      let action = '',
        isRedirect = false,
        isSuccess = false,
        redirectUrl = '/';
      const { content_type } = contentData;

      if (contentAction === 'copy') {
        const newContentId = await this.contentService.copyContent(contentData, user);
        if (newContentId) {
          isSuccess = true;
          redirectUrl = customContentRoutes[content_type].editPath.replace('{contentId}', newContentId);
          isRedirect = true;
        }
        action = 'copied';
      }

      if (contentAction === 'flag') {
        isSuccess = await this.contentService.flagContent(contentId, true, contentData, user);
        action = 'flagged';
      }

      if (contentAction === 'export') {
        isSuccess = await this.contentService.exportContent(contentId, contentData, user, toggles, exportFormat);
        action = 'exported';
        isRedirect = true;
        redirectUrl = '#';
      }

      if (isSuccess) {
        const contentType = content_type || 'testlet';
        toast.success(`Successfully ${action} the ${contentType}`);
        !isRedirect && this.refreshPage();
        isRedirect && history.push(redirectUrl);
      } else {
        throw new Error(`there was an error`);
      }

      return isSuccess;
    } catch (error) {
      let toastMessage = 'This action cannot be performed at the moment';

      if (error.message) {
        toastMessage += ' because ' + error.message;
      }

      toast.error(toastMessage);
    }

    return;
  }

  render() {
    const {
      hasMore,
      hasMoreSearch,
      loading,
      matchCount,
      randomExporting,
      randomExportOpen,
      randomLoading,
      randomSet,
      searching,
      searchResults,
      testlets
    } = this.state;
    const { toggles, user } = this.context;
    const isUserAdmin = isUserAccessOfTypeAdmin(user && user.access_type);
    const isRandomSetAvailable = isFeatureActive(featureToggles.isRandomContentExportEnabled, toggles);
    const displayTestlets = randomSet ? randomSet.elements : searchResults ? searchResults : testlets;
    // appending paged results to a drawn set would stop it being that set
    const loadMoreResults = randomSet ? false : searching ? hasMoreSearch : hasMore;
    const randomExportText = randomSet
      ? `These ${randomSet.questionCount} questions will be exported as a single zip file, with any images they use.` +
        (randomSet.questionCount > largeSetQuestionCount
          ? ' A set this large can take a few minutes to prepare, so please keep this page open.'
          : '')
      : '';

    return (
      <Fragment>
        <div className="min-vh-75 min-vh-lg-100">
          <div className="mb-lg-4 mb-2 border-bottom border-400 pb-3">
            <div className="pl-2">
              <div className="pr-2 pt-2 pb-2">
                <div className="title search-page-title pr-2 pt-2 pb-2">
                  <h1 className="h3">Search Content</h1>
                </div>
              </div>

              <Row className="ml-2 mt-3 mb-1">
                <InlineFilters filterTestlets={this.filterTestlets} clearFilters={this.clearSearch} />
              </Row>
              <SearchBox searchWithKey={this.searchTestlets} clearSearch={this.clearSearch} />
            </div>
          </div>
          {isRandomSetAvailable && (
            <RandomSetBar
              isDisabled={loading || randomLoading}
              isUserAdmin={isUserAdmin}
              matchCount={matchCount}
              onClear={this.clearRandomSet}
              onExport={this.toggleRandomExport}
              onPick={this.pickRandomSet}
              onReroll={this.rerollRandomSet}
              randomSet={randomSet}
            />
          )}
          <DialogBox
            dialogDetails={{
              id: 'exportRandomSet',
              dialogToggleFlag: randomExportOpen,
              title: 'Export Random Set',
              bodyText: randomExportText,
              buttonText: 'Confirm',
              buttonColor: 'success',
              dialogActionButton: this.exportRandomSet,
              toggleDialogFunction: this.toggleRandomExport
            }}
            isLoading={randomExporting}
          />
          <DisplayTestlets
            isLoading={loading || randomLoading}
            reachedBottom={loadMoreResults ? this.loadMore : () => {}}
            testlets={displayTestlets}
            isSearch={searching}
          />
          {!loading && loadMoreResults && <ScrollDownTooltip />}
        </div>
      </Fragment>
    );
  }
}

SearchPage.contextType = UserContext;

SearchPage.propTypes = {
  history: PropTypes.object
};

export default SearchPage;
